import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { customerMenu } from "@/config/menus";
import { PageHeader } from "@/components/KpiCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

const PROPERTY_TYPES = ["Housing Society", "Hotel", "Hospital", "Factory", "Warehouse", "Office", "School", "Retail Store", "Construction Site", "Event", "Individual/Residence"];
const STEPS = ["Contact", "Requirement", "Coverage"];

export default function PostRequirement() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [cats, setCats] = useState([]);
  const [cities, setCities] = useState([]);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    customer_name: user?.name || "", company_name: "", mobile: "", email: user?.email || "",
    location: "", pincode: "", property_type: "", security_requirement: "", num_guards: 5,
    gender_pref: "Any", armed: "Unarmed", shift_duration: 12, num_shifts: 1,
    supervisor_required: false, cctv_required: false, patrolling_required: false,
    contract_duration_months: 12, start_date: "", budget_range: "", special_instructions: "",
  });

  useEffect(() => {
    api.get("/services").then((r) => setCats([...new Set(r.data.flatMap((c) => c.services))])).catch(() => {});
    api.get("/cities").then((r) => setCities(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const canNext = () => {
    if (step === 0) return f.customer_name && f.mobile && f.location;
    if (step === 1) return f.security_requirement && f.num_guards > 0;
    return true;
  };

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/requirements", { ...f, num_guards: Number(f.num_guards), shift_duration: Number(f.shift_duration), num_shifts: Number(f.num_shifts), contract_duration_months: Number(f.contract_duration_months) });
      toast.success("Your requirement has been sent to verified security agencies in your selected area.");
      navigate(`/requirements/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit requirement");
    } finally { setSaving(false); }
  };

  return (
    <DashboardShell menu={customerMenu} title="Post a Requirement">
      <PageHeader title="Request Security Quotations" subtitle="Tell us what you need — verified agencies will respond with competitive quotes." />
      <div className="max-w-3xl">
        {/* stepper */}
        <div className="flex items-center gap-2 mb-8">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2 flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${i < step ? "bg-emerald-500 text-white" : i === step ? "bg-brand text-white" : "bg-navy-200 text-navy-500"}`}>
                {i < step ? <Check size={16} /> : i + 1}
              </div>
              <span className={`text-sm font-medium ${i === step ? "text-navy-900" : "text-navy-400"}`}>{s}</span>
              {i < STEPS.length - 1 && <div className="flex-1 h-px bg-navy-200" />}
            </div>
          ))}
        </div>

        <div className="bg-white border border-navy-200 rounded-xl p-6 space-y-5">
          {step === 0 && (
            <div className="grid sm:grid-cols-2 gap-4">
              <Fld label="Your Name *"><Input value={f.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="req-name" /></Fld>
              <Fld label="Company / Society Name"><Input value={f.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="req-company" /></Fld>
              <Fld label="Mobile Number *"><Input value={f.mobile} onChange={(e) => set("mobile", e.target.value)} data-testid="req-mobile" /></Fld>
              <Fld label="Email"><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="req-email" /></Fld>
              <Fld label="Location / City *">
                <Select value={f.location} onValueChange={(v) => set("location", v)}>
                  <SelectTrigger data-testid="req-location"><SelectValue placeholder="Select city" /></SelectTrigger>
                  <SelectContent>{cities.map((c) => <SelectItem key={c.city} value={c.city}>{c.city}, {c.state}</SelectItem>)}</SelectContent>
                </Select>
              </Fld>
              <Fld label="Pincode"><Input value={f.pincode} onChange={(e) => set("pincode", e.target.value)} data-testid="req-pincode" /></Fld>
              <Fld label="Property Type">
                <Select value={f.property_type} onValueChange={(v) => set("property_type", v)}>
                  <SelectTrigger data-testid="req-property"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>{PROPERTY_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </Fld>
            </div>
          )}
          {step === 1 && (
            <div className="grid sm:grid-cols-2 gap-4">
              <Fld label="Security Requirement *">
                <Select value={f.security_requirement} onValueChange={(v) => set("security_requirement", v)}>
                  <SelectTrigger data-testid="req-category"><SelectValue placeholder="Select service" /></SelectTrigger>
                  <SelectContent className="max-h-72">{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Fld>
              <Fld label="Number of Guards *"><Input type="number" min={1} value={f.num_guards} onChange={(e) => set("num_guards", e.target.value)} data-testid="req-guards" /></Fld>
              <Fld label="Gender Preference">
                <Select value={f.gender_pref} onValueChange={(v) => set("gender_pref", v)}>
                  <SelectTrigger data-testid="req-gender"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Any", "Male", "Female"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                </Select>
              </Fld>
              <Fld label="Armed / Unarmed">
                <Select value={f.armed} onValueChange={(v) => set("armed", v)}>
                  <SelectTrigger data-testid="req-armed"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Unarmed", "Armed"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                </Select>
              </Fld>
              <Fld label="Shift Duration (hours)">
                <Select value={String(f.shift_duration)} onValueChange={(v) => set("shift_duration", v)}>
                  <SelectTrigger data-testid="req-shift"><SelectValue /></SelectTrigger>
                  <SelectContent>{[8, 12, 24].map((g) => <SelectItem key={g} value={String(g)}>{g} hours</SelectItem>)}</SelectContent>
                </Select>
              </Fld>
              <Fld label="Number of Shifts"><Input type="number" min={1} max={3} value={f.num_shifts} onChange={(e) => set("num_shifts", e.target.value)} data-testid="req-shifts" /></Fld>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-5">
              <div className="grid sm:grid-cols-3 gap-4">
                {[["supervisor_required", "Supervisor Required"], ["cctv_required", "CCTV Required"], ["patrolling_required", "Patrolling Required"]].map(([k, l]) => (
                  <label key={k} className="flex items-center justify-between border border-navy-200 rounded-lg px-4 py-3">
                    <span className="text-sm font-medium text-navy-700">{l}</span>
                    <Switch checked={f[k]} onCheckedChange={(v) => set(k, v)} data-testid={`req-${k}`} />
                  </label>
                ))}
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <Fld label="Contract Duration (months)"><Input type="number" min={1} value={f.contract_duration_months} onChange={(e) => set("contract_duration_months", e.target.value)} data-testid="req-duration" /></Fld>
                <Fld label="Start Date"><Input type="date" value={f.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="req-start" /></Fld>
                <Fld label="Budget Range"><Input placeholder="e.g. ₹1.5L - 2L / month" value={f.budget_range} onChange={(e) => set("budget_range", e.target.value)} data-testid="req-budget" /></Fld>
              </div>
              <Fld label="Special Instructions"><Textarea rows={3} value={f.special_instructions} onChange={(e) => set("special_instructions", e.target.value)} data-testid="req-instructions" /></Fld>
            </div>
          )}

          <div className="flex justify-between pt-2">
            <Button variant="outline" className="border-navy-300" disabled={step === 0} onClick={() => setStep(step - 1)}><ArrowLeft size={16} className="mr-1" /> Back</Button>
            {step < STEPS.length - 1 ? (
              <Button className="bg-navy-900 hover:bg-navy-800" disabled={!canNext()} onClick={() => setStep(step + 1)} data-testid="req-next">Continue <ArrowRight size={16} className="ml-1" /></Button>
            ) : (
              <Button className="bg-brand hover:bg-brand-600" disabled={saving} onClick={submit} data-testid="req-submit"><ShieldCheck size={16} className="mr-1" /> {saving ? "Sending…" : "Request Quotations"}</Button>
            )}
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}

function Fld({ label, children }) {
  return <div><Label className="text-xs font-semibold text-navy-600 mb-1.5 block">{label}</Label>{children}</div>;
}
